/**
 * DraftBadge — small status pill showing whether the workflow is in
 * Draft, Template, or Published mode.
 *
 * Reads from `DraftModeContext` when available and falls back to
 * `useDraftModeOptional` defaults otherwise. All state can be overridden
 * via props for testing and isolated rendering.
 *
 * Variants (controlled by `appearance`):
 * - `'filled'` (default) — brand/green filled pill, used historically by
 *   the floating command bar.
 * - `'outline'` — subtle outline pill used in the workflow sub-header.
 *   The Published state additionally uses a green palette outline.
 *
 * A tiny spinner renders next to the pill while `isAutoSaving` is true.
 * The tooltip surfaces autosave status using a relative time format like
 * "Autosaved a few seconds ago".
 */

import { Badge, Tooltip, Spinner, makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';
import { useDraftModeOptional } from '@/contexts/DraftModeContext';
import { useRelativeTime } from '@/utils/dateTime';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  badge: {
    cursor: 'default',
  },
  draftBadge: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  publishedBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  // Outline variant — sub-header pill next to the workflow name.
  // Draft: neutral foreground3 on neutralStroke2 (per spec).
  // Published: green palette for at-a-glance distinction while keeping
  // the same rounded outline silhouette.
  outlineBadge: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  outlineBadgePublished: {
    color: tokens.colorPaletteGreenForeground1,
    borderTopColor: tokens.colorPaletteGreenBorder1,
    borderRightColor: tokens.colorPaletteGreenBorder1,
    borderBottomColor: tokens.colorPaletteGreenBorder1,
    borderLeftColor: tokens.colorPaletteGreenBorder1,
  },
});

export interface DraftBadgeProps {
  /** Override for draft mode state (uses context if not provided) */
  isDraftMode?: boolean;
  /** Override for equality check (uses context if not provided) */
  areProdAndDraftEqual?: boolean;
  /** Override for last saved time (uses context if not provided) */
  lastSavedTime?: Date | null;
  /** Override for saving state (uses context if not provided) */
  isAutoSaving?: boolean;
  /**
   * Visual appearance of the underlying Fluent Badge.
   * - `'filled'` (default) keeps the original brand/green pill used by the floating command bar.
   * - `'outline'` renders a subtle outline pill — used in the workflow sub-header next to the title.
   */
  appearance?: 'filled' | 'outline';
}

export const DraftBadge: React.FC<DraftBadgeProps> = ({
  isDraftMode: isDraftModeOverride,
  areProdAndDraftEqual: areProdAndDraftEqualOverride,
  lastSavedTime: lastSavedTimeOverride,
  isAutoSaving: isAutoSavingOverride,
  appearance = 'filled',
}) => {
  const styles = useStyles();
  const draftMode = useDraftModeOptional();

  // Use overrides if provided, otherwise use context
  const isDraftMode = isDraftModeOverride ?? draftMode?.isDraftMode ?? false;
  const areProdAndDraftEqual = areProdAndDraftEqualOverride ?? draftMode?.areProdAndDraftEqual ?? true;
  const lastSavedTime = lastSavedTimeOverride ?? draftMode?.lastSavedTime ?? null;
  const isAutoSaving = isAutoSavingOverride ?? draftMode?.isAutoSaving ?? false;

  // Self-ticking relative-time label (10s cadence). The hook tears down
  // its interval automatically when `lastSavedTime` is null or the
  // component unmounts.
  const relativeTime = useRelativeTime(lastSavedTime, { style: 'phrase' });

  const badgeText = isDraftMode ? 'Draft' : 'Published';

  // Build tooltip text with save status using relative time
  let tooltipText: string;
  if (isDraftMode) {
    if (isAutoSaving) {
      tooltipText = 'Saving...';
    } else if (lastSavedTime) {
      tooltipText = `Autosaved ${relativeTime}`;
    } else if (areProdAndDraftEqual) {
      tooltipText = 'Draft matches published version';
    } else {
      tooltipText = 'Draft has unpublished changes';
    }
  } else {
    tooltipText = 'Viewing published version (read-only)';
  }

  return (
    <Tooltip content={tooltipText} relationship="description">
      <div className={styles.container}>
        {isAutoSaving && <Spinner size="tiny" />}
        <Badge
          className={mergeClasses(
            styles.badge,
            appearance === 'filled' && (isDraftMode ? styles.draftBadge : styles.publishedBadge),
            appearance === 'outline' && styles.outlineBadge,
            appearance === 'outline' && !isDraftMode && styles.outlineBadgePublished,
          )}
          appearance={appearance}
          size="large"
          shape={appearance === 'outline' ? 'rounded' : 'circular'}
        >
          {badgeText}
        </Badge>
      </div>
    </Tooltip>
  );
};

export default DraftBadge;
